import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { createTag } from '../utils';

/**
 * Hook for managing input bar tags state
 * @param {Object} options - Configuration options
 * @param {Array} options.value - Initial tags value
 * @param {number} options.limit - Maximum number of tags
 * @param {string} options.limitationExceededMsg - Message when limit exceeded
 * @param {Function} options.onChange - Callback when tags change
 * @returns {Object} Tags state and handlers
 */
const useInputTags = ({ value = [], limit = 100, limitationExceededMsg, onChange }) => {
  const [tags, setTags] = useState(value);

  // Sync external value changes
  useEffect(() => {
    setTags(value);
  }, [value]);

  // Execute limit constraint - returns a new array if limit exceeded
  const executeLimitConstraint = useCallback(
    (tagsList) => {
      if (tagsList.length > limit) {
        const msg = limitationExceededMsg || `Sorry, the interest bar is limited to ${limit} seed interests`;
        console.warn(msg);
        // Return sliced array instead of mutating
        return tagsList.slice(0, limit);
      }
      return tagsList;
    },
    [limit, limitationExceededMsg]
  );

  // Remove a single tag
  const removeTag = useCallback(
    (index, e) => {
      e.preventDefault();
      e.stopPropagation();

      const newTags = [...tags];
      newTags.splice(index, 1);
      setTags(newTags);
      if (onChange) onChange(newTags);
    },
    [tags, onChange]
  );

  // Clear all tags
  const clearAll = useCallback(() => {
    setTags([]);
    if (onChange) onChange([]);
  }, [onChange]);

  // Convert input text to tag objects
  const convertInputToTags = useCallback(
    ({ inputValue, suggestToAdd, currentTag, customSuggestions, splitDelimiter, finalCommit = false }) => {
      let textValue = inputValue;
      if (suggestToAdd) {
        const parts = textValue.split(splitDelimiter);
        parts[parts.length - 1] = suggestToAdd.trim();
        textValue = parts.join(splitDelimiter);
      }

      let tagsTextsCandidate = textValue
        .split(splitDelimiter)
        .map((text) => text.trim())
        .filter((text) => text);

      // Remove duplicates
      const tagsLength = tagsTextsCandidate.length;
      tagsTextsCandidate = _.uniqBy(tagsTextsCandidate, _.toLower);

      // Handle duplicate suggestion
      if (suggestToAdd && !finalCommit && tagsTextsCandidate.length !== tagsLength) {
        tagsTextsCandidate.push(currentTag.trim());
      }

      // Create tag objects
      const newTags = [];
      tagsTextsCandidate.forEach((tagText) => {
        const foundIndex = tags.findIndex((tag) => tag.text === tagText);

        if (foundIndex === -1) {
          if (customSuggestions.length > 0) {
            const suggestion = customSuggestions.find((s) => s.text.toLowerCase() === tagText.toLowerCase());
            if (suggestion) {
              newTags.push(createTag(suggestion.text, suggestion.id, 'term-default', false));
            }
          } else {
            const newTag = createTag(tagText, null, 'term-waiting', false);
            newTag.waiting = true;
            newTags.push(newTag);
          }
        } else {
          newTags.push(tags[foundIndex]);
        }
      });

      return newTags;
    },
    [tags]
  );

  return {
    tags,
    setTags,
    removeTag,
    clearAll,
    executeLimitConstraint,
    convertInputToTags,
  };
};

export default useInputTags;
